import { Injectable, inject } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

import { keys } from '@clubhub/constants';
import { LenderPickerComponent } from '@lenderhub/components/lender-picker/lender-picker.component';
import { applicationPaths, roles } from '@shared/constants';
import { ITwoFactorVerificationService, LenderName, LenderNameLender, LenderProductHandlerType, ProductCollectionType, ProductUploadType, TWO_FACTOR_VERIFICATION_SERVICE } from '@shared/models';
import { ContextOption } from '@shared/models/contexts';
import { AuthorizeService } from '@shared/services/authorize.service';
import { LenderHubDataService } from '@shared/services/lenderhub-data.service';
import { LendingTypeService } from '@shared/services/lending-type.service';
import { ModalService } from '@shared/services/modal.service';
import { ProductService } from '@shared/services/product.service';
import { Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LenderHubService {
  private readonly router = inject(Router);
  private readonly lendingTypeService = inject(LendingTypeService);
  private readonly lenderHubDataService = inject(LenderHubDataService);
  private readonly authorizeService = inject(AuthorizeService);
  private readonly productService = inject(ProductService);
  private readonly modalService = inject(ModalService);
  public readonly twoFactorVerificationService = inject<ITwoFactorVerificationService>(TWO_FACTOR_VERIFICATION_SERVICE);

  public currentIssue: ContextOption | undefined;
  public currentContext: ContextOption | undefined;
  public lenderName: LenderName | null = null;
  public lenderNames: LenderName[] | null = null;
  public isAdmin = false;
  public lenderLoaded$ = new Subject();

  public constructor() {
    const lenderHubDataService = this.lenderHubDataService;
    const authorizeService = this.authorizeService;

    authorizeService
      .isAuthenticated()
      .pipe(distinctUntilChanged())
      .subscribe(isAuthenticated => {
        this.isAdmin = authorizeService.hasRole(roles.formManagement);
        if (isAuthenticated && !this.isAdmin) {
          lenderHubDataService.get<LenderName[]>('LenderName/userLenderNames').subscribe(lenderNames => {
            this.lenderNames = lenderNames;
            if (!this.lenderNames) {
              return;
            }

            if (lenderNames.length < 2) {
              // Select first in case when user has only one lender
              this.setLenderName(lenderNames[0]);
              return;
            }

            const primaryLenderNameId = this.authorizeService.primaryLenderNameId;
            if (primaryLenderNameId) {
              const selectedLender = this.lenderNames.find(r => r.lenderNameId === primaryLenderNameId);
              if (selectedLender) {
                this.setLenderName(selectedLender);
                return;
              }
            }

            this.modalService
              .open({
                size: 'md',
                title: 'Select lender',
                data: lenderNames,
                sticky: true,
                component: LenderPickerComponent,
              })
              .then(
                (selectedLenderNameId: number) => {
                  const selectedLender = this.lenderNames?.find(r => r.lenderNameId === selectedLenderNameId);
                  if (!selectedLender) {
                    return;
                  }

                  lenderHubDataService.put('LenderName/applyPrimaryLender', selectedLender.lenderNameId).subscribe(async () => {
                    this.setLenderName(selectedLender);
                    await this.authorizeService.setPrimaryLenderNameId(selectedLender.lenderNameId);
                    // Need to redirect to home page to set all lender's data
                    window.location.replace('');
                  });
                },
                () => {
                  this.authorizeService.signOut('');
                  // redirect to log out page
                  window.location.replace(applicationPaths.logOut);
                },
              );
          });
        }
      });
  }

  private setLenderName(lenderName: LenderName) {
    this.lenderName = lenderName;
    this.updateProductsStatuses();
    this.lenderLoaded$.next(true);
  }

  public get lender(): LenderNameLender | undefined {
    return this.lenderName?.lenders.find(x => x.lendingTypeId === this.lendingTypeService.lendingType?.id);
  }

  public getLenderByLendingType(lendingTypeCode: string) {
    const lendingType = this.lendingTypeService.getLendingTypeByCode(lendingTypeCode);
    if (!lendingType) {
      return;
    }

    return this.lenderName?.lenders.find(x => x.lendingTypeId === lendingType.id);
  }

  public get productsHandlerType(): LenderProductHandlerType | undefined {
    return this.lenderName?.resiBtlProductHandlerType;
  }

  public getLenderNameById(lenderNameId: number): Observable<LenderName> {
    sessionStorage.setItem(keys.lenderNameId, lenderNameId.toString());
    return this.lenderHubDataService.get<LenderName>(`LenderName/Admin?lenderNameId=${lenderNameId}`).pipe(
      tap(lenderName => {
        this.lenderName = lenderName;
        // this.updateProductsStatuses();
      }),
    );
  }

  public navigateCriteriaHome() {
    this.router.navigate(['criteria']);
  }

  public navigateCriteriaV2Home() {
    this.router.navigate([
      this.isAdmin ? `ignite/criteria/landing/${this.lendingTypeService.lendingType?.id}/lenders` : 'criteria-v2/dashboard',
    ]);
  }

  public navigateCriteriaIssues() {
    this.router.navigate([`criteria/${this.lendingTypeService.lendingType?.id}/issues`]);
  }

  public navigateCriteriaContexts(issueId: number, searchTerm: string) {
    this.navigate(`issues/${issueId}/contexts`, {
      queryParams: { searchTerm },
    });
  }

  public navigateCriteriaUpdateContext(issueId: number, contextId: number, searchTerm: string) {
    this.navigate(`issues/${issueId}/contexts/${contextId}`, {
      queryParams: { searchTerm },
    });
  }

  public navigateCriteriaUnverifiedContexts(lendingTypeId: number | undefined) {
    this.router.navigate([`/criteria/${lendingTypeId}/unverified-contexts`]);
  }

  public navigate(url: string, navigationOptions: NavigationExtras | null = null) {
    let defaultNavigationExtras: NavigationExtras = {};

    if (navigationOptions) {
      defaultNavigationExtras = {
        ...defaultNavigationExtras,
        ...navigationOptions,
      };

      if (navigationOptions.queryParams && Object.values(navigationOptions.queryParams).filter(v => v).length === 0) {
        defaultNavigationExtras.queryParams = {};
      }
    }

    // https://github.com/angular/angular/issues/18798#issuecomment-385526236
    const urlTree = this.router.createUrlTree([`/criteria/${this.lendingTypeService.lendingType?.id}/${url}`], defaultNavigationExtras);
    this.router.navigateByUrl(urlTree, defaultNavigationExtras);
  }

  public updateProductsStatuses() {
    if (this.lenderName?.hasProducts) {
      combineLatest([
        this.productService.getScheduled(ProductCollectionType.Bridging, ProductUploadType.Core),
        this.productService.getScheduled(ProductCollectionType.ResiBtl, ProductUploadType.Core),
      ]).subscribe(([bridgingSchedules, resBtlSchedules]) => {
        if (!!this.lenderName) {
          this.lenderName.hasSchedule = (bridgingSchedules?.future?.length ?? 0) > 0 || (resBtlSchedules?.future?.length ?? 0) > 0;
        }
      });

      combineLatest([
        this.productService.getHasLiveProducts(ProductCollectionType.Bridging, ProductUploadType.Core),
        this.productService.getHasLiveProducts(ProductCollectionType.ResiBtl, ProductUploadType.Core),
      ]).subscribe(hasLiveProducts => {
        if (!!this.lenderName) {
          this.lenderName.hasLiveProducts = hasLiveProducts[0] || hasLiveProducts[1];
        }
      });
    }
  }
}
